/**
 * HandlerConfigForm - schema-driven form for a single side-effect
 * handler's `config_schema`.
 *
 * The backend's `/admin/effect-handlers/` endpoint returns each
 * handler's raw `config_schema` (typically a JSON-Schema-ish dict of
 * `{field_name: {type, required, description, ...}}`). We render a
 * matching input per field - text, number, boolean, enum-style select,
 * and a JSON-object fallback for anything we can't render natively.
 *
 * The config JSON is untyped (`Record<string, unknown>`) so the form
 * writes back a plain object to the parent.
 */

import { useMemo } from 'react'

import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

import { JsonObjectInput } from './json-object-input'

interface SchemaField {
  name: string
  type: string
  required?: boolean
  description?: string
  choices?: unknown[]
  default?: unknown
}

interface HandlerConfigFormProps {
  schema: Record<string, unknown> | undefined
  value: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
}

export function HandlerConfigForm({
  schema,
  value,
  onChange,
}: HandlerConfigFormProps) {
  const fields = useMemo(() => parseSchema(schema), [schema])

  const rawJson = useMemo(() => JSON.stringify(value ?? {}, null, 2), [value])

  if (fields.length === 0) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Config (JSON object)
        </Label>
        <p className="text-[10px] text-[var(--ink-3)]">
          This handler exposes no structured schema - edit the raw JSON
          configuration.
        </p>
        <JsonObjectInput
          key={rawJson}
          value={rawJson}
          onCommit={onChange}
          minHeight={72}
        />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((field) => (
        <SchemaInput
          key={field.name}
          field={field}
          value={value?.[field.name]}
          onChange={(next) => onChange({ ...value, [field.name]: next })}
        />
      ))}
    </div>
  )
}

function SchemaInput({
  field,
  value,
  onChange,
}: {
  field: SchemaField
  value: unknown
  onChange: (next: unknown) => void
}) {
  const kind = inferKind(field)

  if (kind === 'boolean') {
    return (
      <div className="col-span-1 flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        <Label className="text-[11px] text-[var(--ink-2)]">
          {humanize(field.name)}
          {field.required && (
            <span className="text-[var(--status-error)]"> *</span>
          )}
        </Label>
      </div>
    )
  }

  if (kind === 'enum' && field.choices && field.choices.length > 0) {
    return (
      <div className="col-span-1 flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {humanize(field.name)}
          {field.required && (
            <span className="text-[var(--status-error)]"> *</span>
          )}
        </Label>
        <Select
          value={value == null ? '' : String(value)}
          onValueChange={(next: string) => onChange(next)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {field.choices.map((choice) => (
              <SelectItem key={String(choice)} value={String(choice)}>
                {String(choice)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.description && (
          <p className="text-[10px] text-[var(--ink-3)]">{field.description}</p>
        )}
      </div>
    )
  }

  if (kind === 'number') {
    return (
      <div className="col-span-1 flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {humanize(field.name)}
          {field.required && (
            <span className="text-[var(--status-error)]"> *</span>
          )}
        </Label>
        <Input
          type="number"
          value={value == null ? '' : String(value)}
          onChange={(e) => {
            const raw = e.target.value
            onChange(raw === '' ? null : Number(raw))
          }}
        />
        {field.description && (
          <p className="text-[10px] text-[var(--ink-3)]">{field.description}</p>
        )}
      </div>
    )
  }

  if (kind === 'json') {
    const json = JSON.stringify(
      (value ?? (kind === 'json' ? {} : '')) as unknown,
      null,
      2,
    )
    return (
      <div className="col-span-2 flex flex-col gap-1">
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {humanize(field.name)}
          {field.required && (
            <span className="text-[var(--status-error)]"> *</span>
          )}
        </Label>
        <JsonObjectInput
          key={json}
          value={json}
          onCommit={onChange}
          minHeight={72}
        />
        {field.description && (
          <p className="text-[10px] text-[var(--ink-3)]">{field.description}</p>
        )}
      </div>
    )
  }

  return (
    <div className="col-span-1 flex flex-col gap-1">
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {humanize(field.name)}
        {field.required && (
          <span className="text-[var(--status-error)]"> *</span>
        )}
      </Label>
      <Input
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.description && (
        <p className="text-[10px] text-[var(--ink-3)]">{field.description}</p>
      )}
    </div>
  )
}

function parseSchema(schema: Record<string, unknown> | undefined): SchemaField[] {
  if (!schema || typeof schema !== 'object') return []
  const properties =
    schema.properties && typeof schema.properties === 'object'
      ? (schema.properties as Record<string, unknown>)
      : schema
  const required = Array.isArray(schema.required)
    ? schema.required.map(String)
    : []
  const entries = Object.entries(properties)
  return entries
    .filter(([, spec]) => spec && typeof spec === 'object')
    .map(([name, raw]) => {
      const spec = raw as Record<string, unknown>
      return {
        name,
        type: String(spec.type ?? inferSchemaType(spec) ?? 'string'),
        required: Boolean(spec.required) || required.includes(name),
        description: typeof spec.description === 'string' ? spec.description : undefined,
        choices: Array.isArray(spec.choices) ? spec.choices : undefined,
        default: spec.default,
      }
    })
}

function inferSchemaType(spec: Record<string, unknown>): string | undefined {
  const oneOf = spec.oneOf
  if (!Array.isArray(oneOf)) return undefined
  const types = oneOf
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
    .map((entry) => entry.type)
    .filter((type): type is string => typeof type === 'string')
  if (types.includes('string')) return 'string'
  if (types.includes('array')) return 'array'
  return types[0]
}

function inferKind(field: SchemaField): 'boolean' | 'number' | 'enum' | 'json' | 'text' {
  if (field.choices && field.choices.length > 0) return 'enum'
  const t = field.type.toLowerCase()
  if (t === 'boolean' || t === 'bool') return 'boolean'
  if (t === 'number' || t === 'integer' || t === 'int' || t === 'float') return 'number'
  if (t === 'object' || t === 'dict' || t === 'array' || t === 'list') return 'json'
  return 'text'
}

function humanize(snake: string): string {
  return snake.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
